import { useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  GENERIC_ERROR_MESSAGE,
  getApiStatusMessage,
  getUserErrorMessage,
} from './error-messages';

const API_BASE_URL = `${window.location.origin}/api`;

export interface JobStatusApiResponse {
  id: string;
  job_id: string;
  status: string;
  created_at: string;
  updated_at: string;
}

// --- SUBSCRIPTION API TYPES ---
export interface SubscriptionInfo {
  id: string;
  user_id?: string;
  stripe_subscription_id?: string;
  status?: string;
  price_id?: string;
  cancel_at_period_end?: boolean;
  current_period_start?: string;
  current_period_end?: string;
  plan_amount?: number;
  plan_interval?: string;
  created_at?: string;
  updated_at?: string;
}

export interface SubscriptionGetResponse {
  success: boolean;
  subscription: SubscriptionInfo | null;
}

export interface SubscriptionCheckoutResponse {
  success: boolean;
  checkout_url: string;
}

export interface MarkOneWeekOfferSeenResponse {
  success: boolean;
  one_week_offer_seen_at?: string | null;
}

export interface SubscriptionActionResponse {
  success: boolean;
  subscription?: SubscriptionInfo;
}

export interface AvatarPreset {
  id: string;
  url: string;
}

export interface JobNoteApiResponse {
  id: string;
  job_id: string;
  user_id: string;
  note: string;
  created_at: string;
  updated_at: string;
}

export interface JobPersonApiResponse {
  id: string;
  job_id: string;
  user_id: string;
  name: string;
  role?: string;
  created_at: string;
  updated_at: string;
}

export interface JobInterviewApiResponse {
  id: string;
  job_id: string;
  user_id: string;
  interview_date: string; // Assuming datetime comes as string
  notes: string;
  rating: number;
  people: string[];
  created_at: string;
  updated_at: string;
}

export interface JobAnalysisApiResponse {
  id: string;
  job_id: string;
  user_id: string;
  description: string;
  fit_rating: string;
  competitive_advantage: string[];
  areas_for_improvement: string[];
  hiring_manager_thoughts: string[];
  recommendations: string[];
  created_at: string;
  updated_at: string;
}

export interface JobCompleteApiResponse {
  id: string;
  title: string;
  company: string;
  description: string;
  location: string;
  remote: string;
  salary: string;
  raw_posting?: string;
  listing_url?: string;
  interview_focus_areas: string[];
  generation_status: string;
  created_at: string;
  updated_at: string;
  statuses: JobStatusApiResponse[];
  notes: JobNoteApiResponse[];
  persons: JobPersonApiResponse[];
  job_interviews: JobInterviewApiResponse[];
  interviews: InterviewResponse[];
  analysis?: JobAnalysisApiResponse | null;
}

// This existing interface can be used for the simpler /jobs endpoint if needed,
// or deprecated if getJobCompleteById becomes the standard.
export interface JobApiResponse {
  id: string;
  title: string;
  company: string;
  description: string;
  location: string;
  remote: string;
  salary: string;
  raw_posting?: string;
  listing_url?: string;
  interview_focus_areas: string[];
  generation_status: string;
  latest_status?: string | null; // For application status
  created_at: string; // Assuming datetime comes as string
  updated_at: string; // Assuming datetime comes as string
  notes: any[];
  statuses: any[];
  persons: any[];
  job_interviews: any[];
  interviews: any[];
  analysis?: JobAnalysisApiResponse | null;
}

export interface JobResponse {
  id: string;
  title: string;
  company: string;
  description: string;
  location: string;
  remote: string;
  salary: string;
  raw_posting?: string;
  interview_focus_areas?: string[];
  generation_status: string;
  latest_status?: string | null; // For application status
  created_at: string; // Assuming datetime comes as string
  updated_at: string; // Assuming datetime comes as string
}

export interface JobUrlExtractionError {
  code: string;
  message: string;
}

export interface JobNoteCreatePayload {
  note: string;
}

export interface JobPersonNoteCreatePayload {
  note_type: string;
  note: string;
  id?: string;
}


export interface JobPersonCreatePayload {
  name: string;
  role?: string;
  notes?: JobPersonNoteCreatePayload[];
}

export interface JobInterviewCreatePayload {
  interview_date: string; // ISO date string
  notes: string;
  rating: number;
  people?: string[];
}

export interface JobInterviewUpdatePayload extends Partial<JobInterviewCreatePayload> {
  // All fields are optional for updates
}

export interface JobGenerationStatusResponse {
  status: 'NONE' | 'pending' | 'researching' | 'generating' | 'ready';
}

// --- INTERVIEW API TYPES ---
export interface AudioResponse {
  id: string;
  url: string;
}

export interface QuestionResponse {
  question: string;
  expected: string;
  id: string;
  audio?: AudioResponse;
  created_at: string;
  updated_at: string;
}

export interface AnswerResponse {
  id: string;
  question_id: string;
  user_id: string;
  job_id?: string | null;
  interview_id: string;
  response: string;
  created_at: string;
  updated_at: string;
}

export interface QuestionsPayload {
  Question: QuestionResponse;
  Type: string;
  Answer?: AnswerResponse;
  Audio?: AudioResponse;
}

export interface InterviewGenerationRequest {
  job_id?: string;
  questions: number;
  focus_area?: string;
  focus_instructions?: string;
  general_question_categories?: string[];
  general_question_count?: number;
}

export interface InterviewGenerationResponse {
  interview_id: string;
  status: 'created' | 'existing';
  message: string;
}

export interface InterviewQuestionSetAccessResponse {
  questions: number;
  allowed: boolean;
  reason: 'allowed' | 'quota' | 'premium' | 'blocked';
  message: string;
}

export interface InterviewAccessResponse {
  plan: 'free' | 'premium';
  creation_locked: boolean;
  message: string;
  window?: string | null;
  limit?: number | null;
  used: number;
  remaining?: number | null;
  window_end?: string | null;
  question_sets: InterviewQuestionSetAccessResponse[];
}

export interface InterviewResponse {
  id: string;
  user_id: string;
  job_id?: string | null;
  status: string;
  questions: QuestionsPayload[];
  overall_description: string;
  overall_score: number;
  hiring_considerations: string[];
  pros: string[];
  cons: string[];
  suggestions: string[];
  focus_area: string;
  focus_instructions: string;
  general_question_categories: string[];
  general_question_count: number;
  question_order: string[];
  created_at: string;
  updated_at: string;
}

export interface EducationResponse {
  id: string;
  user_id: string;
  institution: string;
  degree_type: string;
  major: string;
  start_date: string;
  end_date: string | null;
  is_ongoing: boolean;
}

export interface SkillCreate {
  name: string;
}

export interface SkillResponse {
  id: string;
  name: string;
}

export interface DetailCreate {
  name: string;
}

export interface DetailResponse {
  id: string;
  content: string;
}

export interface DetailData {
  id?: string;
  content: string;
  employment_id?: string;
  education_id?: string;
  profile_section_id?: string;
}

export interface EmploymentResponse {
  id?: string;
  user_id?: string;
  title: string;
  company: string;
  start_date: string;
  end_date?: string | null;
  is_ongoing?: boolean;
  skills: SkillResponse[];
  details: DetailResponse[];
}


export interface DetailData {
    id?: string;
    user_id?: string;
    content: string;
    education_id?: string;
    employment_id?: string;
    profile_section_id?: string;
}

export interface SkillData {
    id?: string;
    user_id?: string;
    name: string;
    employment_id?: string;
    education_id?: string;
    profile_section_id?: string;
}

export interface ProfileSectionResponse {
    id: string;
    user_id: string;
    title: string;
    section_key?: string | null;
    attributes: ProfileSectionAttribute[];
    suggested_attributes: string[];
}

export interface ProfileSectionAttribute {
    label: string;
    value: string;
}

export interface FeedbackResponse {
    id: string;
    question: string;
    answer: string;
    thought: string;
    reasoning: string;
    grade: number;
    feedback: string;
    created_at: string;
    updated_at: string;
}

// --- PITCH ANALYSIS API TYPES ---
export interface PitchAnalysisCreate {
    content: string;
    time_limit: number; // 30, 60, or 90 seconds
    pitch_type?: string;
    employment_id?: string;
    education_id?: string;
    job_id?: string;
}

export interface PitchAnalysisResponse {
    id: string;
    user_id: string;
    content: string;
    time_limit: string;
    thoughts: string;
    summary: string;
    strengths: string[];
    areas_for_improvement: string[];
    coaching_tips: string[];
    clarity_score: number;
    structure_score: number;
    relevance_score: number;
    overall_score: number;
    status: string; //  grading | complete
    pitch_type: string;
    employment_id: string;
    education_id: string;
    job_id: string;
    created_at: string;
    updated_at: string;
}

// --- SALARY NEGOTIATION API TYPES ---

export interface ScenarioInfo {
    id: string;
    label: string;
    category: string;
    site_label: string;
}

export interface PersonalityInfo {
    id: string;
    name: string;
    label: string;
    explanation: string;
    image_url: string;
}

export interface NegotiationMessage {
    role: 'user' | 'assistant';
    content: string;
    created_at: string;
    reasoning?: string;
    hiring_manager_thoughts?: string;
    audio_url?: string;
}

export interface SalaryNegotiationCreate {
    job_id?: string;
    scenario_type: string;
    scenario_config?: Record<string, any>;
    initial_user_message?: string;
    listed_salary?: string;
    desired_salary?: string;
    personality?: 'friendly' | 'corporate_neutral' | 'aggressive' | 'data_driven';
}

export interface SalaryNegotiationTurnRequest {
    message: string;
}

export interface SalaryNegotiationResponse {
    id: string;
    user_id: string;
    job_id?: string | null;
    scenario_type: string;
    scenario_config: Record<string, any>;
    messages: NegotiationMessage[];
    status: string;
    outcome?: string | null;
    thoughts: string;
    summary: string;
    strengths: string[];
    areas_for_improvement: string[];
    coaching_tips: string[];
    strategy_score?: number | null;
    clarity_score?: number | null;
    relationship_score?: number | null;
    overall_score?: number | null;
    created_at: string;
    updated_at: string;
}

// --- TODO API TYPES ---
export interface TodoCreate {
    title: string;
    description?: string;
    job_id?: string;
    priority?: 'low' | 'medium' | 'high';
    due_date?: string; // ISO date string
    remind_at?: string; // ISO date string
    tags?: string[];
}

export interface TodoUpdate {
    title?: string;
    description?: string;
    job_id?: string;
    priority?: 'low' | 'medium' | 'high';
    due_date?: string; // ISO date string
    remind_at?: string; // ISO date string
    completed?: boolean;
    tags?: string[];
}

export interface TodoResponse {
    id: string;
    title: string;
    description: string;
    user_id: string;
    job_id?: string;
    job_title?: string;
    job_company?: string;
    priority: 'low' | 'medium' | 'high';
    due_date?: string; // ISO date string
    remind_at?: string; // ISO date string
    completed: boolean;
    tags: string[];
    created_at: string;
    updated_at: string;
    completed_at?: string;
}

export interface DashboardOverviewResponse {
    jobs: DashboardJobSummary[];
    recent_interviews: DashboardInterviewSummary[];
    stats: {
      total_jobs: number;
      completed_interviews: number;
      pending_interviews: number;
      profile_ready: string;
    };
    todos_overdue: TodoResponse[];
    todos_due_soon: TodoResponse[];
    pitches: PitchAnalysisResponse[];
    cache: {
      cache_hit: boolean;
      refreshed_sections: string[];
      generated_at: string;
    };
}

export interface DashboardInterviewSummary {
    id: string;
    user_id: string;
    job_id?: string;
    status: string;
    overall_score: number;
    created_at: string;
    updated_at: string;
}

export interface DashboardJobSummary {
    id: string;
    title: string;
    company: string;
    description: string;
    location: string;
    remote: string;
    salary: string;
    raw_posting?: string;
    listing_url?: string;
    latest_status?: string | null;
    created_at: string;
    updated_at: string;
    statuses: JobStatusApiResponse[];
    job_interviews: JobInterviewApiResponse[];
    interviews: DashboardInterviewSummary[];
}

// --- ARTICLE API TYPES ---
export interface ArticleListItem {
    slug: string;
    title: string;
    category: string;
    is_public?: boolean;
    description?: string;
    author?: string;
    previous_article?: string; // Slug of previous article in series
}

export interface ArticleResponse {
    slug: string;
    title: string;
    category: string;
    is_public?: boolean;
    description?: string;
    author?: string;
    previous_article?: string; // Slug of previous article
    next_article?: string; // Slug of next article
    content: string; // Raw markdown content
}

export interface ArticleListResponse {
    articles: ArticleListItem[];
    categories: string[];
}

export const useApiClient = () => {
  const { token: authToken, logout } = useAuth();

  const makeRequest = useCallback(async <T,>(
    endpoint: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
    body?: any,
    includeToken: boolean = true
  ): Promise<T> => {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      // Add cache-busting headers for GET requests to prevent stale data
      if (method === 'GET') {
        headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
        headers['Pragma'] = 'no-cache';
        headers['Expires'] = '0';
      }
      
      if (includeToken) {
        if (authToken) {
          headers['Authorization'] = `Bearer ${authToken}`;
        } else {
          console.warn(`No auth token available for ${method} ${endpoint}`);
        }
      }

      const options: RequestInit = {
        method,
        headers,
      };

      let bodyData = undefined;
      if (body && method !== 'GET') {
        bodyData = JSON.stringify(body);
        options.body = bodyData;
      }

      const url = `${API_BASE_URL}${endpoint}`;
      const response = await fetch(url, options);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error(`Request failed with status ${response.status}:`, errorData);

        if (response.status === 401) {
          console.error('Authentication error - token may be invalid or expired');
          // Get current location for return URL
          const currentPath = window.location.pathname + window.location.search;
          // Only redirect if we're not already on the login page
          if (!currentPath.startsWith('/login')) {
            logout(currentPath);
          }
        }
        
        // Only surface actionable client errors. Hide technical/server detail.
        let errorMessage = getApiStatusMessage(response.status);
        if (response.status === 401 && endpoint === '/auth/login') {
          errorMessage = 'Invalid email or password.';
        } else if (response.status === 400 || response.status === 422) {
          const candidate = typeof errorData.detail === 'string'
            ? errorData.detail
            : errorData.detail?.error;
          errorMessage = getUserErrorMessage(
            candidate,
            getApiStatusMessage(response.status)
          );
        }
        const error = new Error(errorMessage);
        // Add status code as a property to the error object
        (error as any).statusCode = response.status;
        // Preserve the detail object if it's an object (for structured errors like 405)
        if (typeof errorData.detail === 'object' && errorData.detail !== null) {
          (error as any).detail = errorData.detail;
        }
        throw error;
      }

      const contentType = response.headers.get("content-type");
      if (response.status === 204) {
        // No Content
        return {} as T;
      }
      if (contentType && contentType.indexOf("application/json") !== -1) {
        const text = await response.text();
        if (!text) {
          // Empty body, treat as empty object
          return {} as T;
        }
        const obj = JSON.parse(text);
        return obj;
      } else {
        return {} as T;
      }
    } catch (error) {
      console.error('API request failed:', error);
      
      // Handle network errors (failed to connect to backend)
      if (error instanceof TypeError && error.message.includes('fetch')) {
        console.error('Network error - failed to connect to backend');
        // Get current location for return URL
        const currentPath = window.location.pathname + window.location.search;
        // Only redirect if we're not already on the login page
        if (!currentPath.startsWith('/login')) {
          logout(currentPath);
        }
      }
      
      throw new Error(getUserErrorMessage(error, GENERIC_ERROR_MESSAGE));
    }
  }, [authToken, logout]);

  const api = useMemo(() => ({
    // Agreements
    acceptAgreementsV1: (acceptPrivacy: boolean, acceptUserAgreement: boolean) =>
      makeRequest<any>(`/auth/agreements/accept`, 'POST', { v1_privacy: acceptPrivacy, v1_user_agreement: acceptUserAgreement }),
    getAvatarPresets: () =>
      makeRequest<AvatarPreset[]>(`/auth/avatar/presets`, 'GET'),
    selectAvatar: (mode: 'none' | 'preset', presetId?: string) =>
      makeRequest<any>(`/auth/avatar/select`, 'PUT', { mode, preset_id: presetId ?? null }),
    uploadAvatar: (formData: FormData) => {
      const headers: Record<string, string> = {};
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
      return fetch(`${API_BASE_URL}/auth/avatar/upload`, {
        method: 'POST',
        headers,
        body: formData,
      }).then(async response => {
        if (!response.ok) {
          throw new Error(getApiStatusMessage(response.status));
        }
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.indexOf("application/json") !== -1) {
          return await response.json();
        }
        return {};
      });
    },
    // Auth: Password reset flow
    requestPasswordReset: (email: string) =>
      makeRequest<{ message: string }>(`/forgot-password`, 'POST', { email }, false),
    verifyPasswordResetToken: (token: string) =>
      makeRequest<{ valid: boolean }>(`/reset-password/verify?token=${encodeURIComponent(token)}`, 'GET', undefined, false),
    resetPassword: (token: string, new_password: string) =>
      makeRequest<{ message: string }>(`/reset-password`, 'POST', { token, new_password }, false),
    purchaseCredits: (pack: string) => makeRequest<{ success: boolean; purchase_id?: string; checkout_url?: string }>('/purchase/credit', 'POST', { pack }),
    getCreditsBalance: () => makeRequest<{ credits: number }>('/credits/balance'),
    cancelPurchase: (purchaseId: string) => makeRequest(`/purchase/${purchaseId}/cancel`, 'POST'),
    getDashboardOverview: (hoursAhead: number = 48, recentLimit: number = 5) =>
      makeRequest<DashboardOverviewResponse>(`/dashboard?hours_ahead=${hoursAhead}&recent_limit=${recentLimit}`),
    getJobs: () => makeRequest<JobApiResponse[]>('/jobs'),
    getJob: (id: string) => makeRequest<JobResponse>(`/job/${id}`, 'GET'),
    createJob: (data: any) => makeRequest<any>('/jobs', 'POST', data),
    extractJobFromListing: (listing: string, posting_source?: string) =>
      makeRequest<any>('/jobs/from_listing', 'POST', { listing, posting_source }),
    extractJobFromUrl: (listing_url: string) =>
      makeRequest<any>('/jobs/from_url', 'POST', { listing_url }),
    extractJobFromUrlViaJina: (listing_url: string) =>
      makeRequest<any>('/jobs/from_url/jina', 'POST', { listing_url }),
    getJobById: (jobId: string) => makeRequest<JobApiResponse>(`/jobs/${jobId}`),
    // Fetch a single job person and their notes
    getJobPersonById: (jobId: string, personId: string) => makeRequest<any>(`/jobs/${jobId}/persons/${personId}`), // Assuming JobApiResponse is for basic job details
    getJobCompleteById: (jobId: string) => makeRequest<JobCompleteApiResponse>(`/jobs/${jobId}/complete`),
    getJobStatusHistory: (jobId: string) => makeRequest<JobStatusApiResponse[]>(`/jobs/${jobId}/status`),

    // Fetch a detailed interview by ID
    getInterviewById: (interviewId: string) => makeRequest<InterviewResponse>(`/interviews/${interviewId}`),
    getInterviewAccess: () => makeRequest<InterviewAccessResponse>(`/interviews/access`),
    getInterviews: (activeOnly: boolean = false) =>
      makeRequest<InterviewResponse[]>(`/interviews${activeOnly ? '?active_only=true' : ''}`),
    // Get all interviews for a specific job
    getInterviewsByJob: (jobId: string) => makeRequest<InterviewResponse[]>(`/interviews/job/${jobId}`),
    // Generate a new interview
    generateInterview: (request: InterviewGenerationRequest) => makeRequest<InterviewGenerationResponse>(`/interviews`, 'POST', request),
    // Save or update an interview answer
    saveInterviewAnswer: (interviewId: string, questionId: string, response: string) =>
      makeRequest<AnswerResponse>(`/interviews/${interviewId}/${questionId}`, 'PUT', { response }),
    // Added for completeness if still used separately
    completeInterview: (interviewId: string) => makeRequest<InterviewResponse>(`/interviews/${interviewId}/complete`, 'POST'),
    getFeedback: (interviewId: string) => makeRequest<FeedbackResponse[]>(`/interviews/${interviewId}/feedback`),
    addJobStatus: (jobId: string, status: string) => makeRequest<JobStatusApiResponse>(`/jobs/${jobId}/status`, 'POST', { status }),
    // Job generation endpoints
    getJobGenerationStatus: (jobId: string) => makeRequest<JobGenerationStatusResponse>(`/jobs/${jobId}/generate/status`, 'GET'),
    generateJob: (jobId: string) => makeRequest<JobGenerationStatusResponse>(`/jobs/${jobId}/generate`, 'POST'),
    deleteJobStatus: (jobId: string, statusId: string) => makeRequest<any>(`/jobs/${jobId}/status/${statusId}`, 'DELETE'),
    updateJob: (jobId: string, data: any) => makeRequest<JobApiResponse>(`/jobs/${jobId}`, 'PUT', data),
    createJobNote: (jobId: string, payload: JobNoteCreatePayload) => makeRequest<JobNoteApiResponse>(`/jobs/${jobId}/notes`, 'POST', payload),
    updateJobNote: (jobId: string, noteId: string, payload: JobNoteCreatePayload) => makeRequest<JobNoteApiResponse>(`/jobs/${jobId}/notes/${noteId}`, 'PUT', payload),
    deleteJobNote: (jobId: string, noteId: string) => makeRequest<void>(`/jobs/${jobId}/notes/${noteId}`, 'DELETE'),
    createJobPerson: (jobId: string, payload: JobPersonCreatePayload) => makeRequest<JobPersonApiResponse>(`/jobs/${jobId}/persons`, 'POST', payload),
    updateJobPerson: (jobId: string, personId: string, payload: JobPersonCreatePayload) => makeRequest<JobPersonApiResponse>(`/jobs/${jobId}/persons/${personId}`, 'PUT', payload),
    deleteJobPerson: (jobId: string, personId: string) => makeRequest<void>(`/jobs/${jobId}/persons/${personId}`, 'DELETE'),
    createJobInterview: (jobId: string, payload: JobInterviewCreatePayload): Promise<JobInterviewApiResponse> => {
      return makeRequest<JobInterviewApiResponse>(`/jobs/${jobId}/interviews`, 'POST', payload);
    },
    updateJobInterview: (jobId: string, interviewId: string, payload: JobInterviewUpdatePayload): Promise<JobInterviewApiResponse> => {
      return makeRequest<JobInterviewApiResponse>(`/jobs/${jobId}/interviews/${interviewId}`, 'PUT', payload);
    },
    deleteJobInterview: (jobId: string, interviewId: string) => 
      makeRequest<void>(`/jobs/${jobId}/interviews/${interviewId}`, 'DELETE'),
    createJobPersonNote: (jobId: string, personId: string, payload: JobPersonNoteCreatePayload) => makeRequest<JobPersonNoteApiResponse>(`/jobs/${jobId}/persons/${personId}/notes`, 'POST', payload),
    deleteJobPersonNote: (jobId: string, personId: string, noteId: string) => makeRequest<void>(`/jobs/${jobId}/persons/${personId}/notes/${noteId}`, 'DELETE'),
    deleteJob: (jobId: string) => makeRequest<void>(`/jobs/${jobId}`, 'DELETE'),
    getJobAnalysis: (jobId: string) => makeRequest<JobAnalysisApiResponse>(`/jobs/${jobId}/resume_analysis`, 'GET'),
    // Trigger resume analysis generation task
    generateResumeAnalysis: (jobId: string) => makeRequest<{ status: string }>(`/jobs/${jobId}/generate_analysis`, 'POST'),

    getQuestions: () => makeRequest<any[]>('/questions'),
    getQuestionsByJobId: (jobId: string) => makeRequest<any[]>(`/questions/job/${jobId}`),
    generateQuestions: (data: { job_id: string; count?: number }) =>
      makeRequest<any[]>('/questions', 'POST', data),

    getFindings: () => makeRequest<any[]>('/findings'),
    getFindingById: (findingId: string) => makeRequest<any>(`/findings/${findingId}`),
    getFindingsByJobId: (jobId: string) => makeRequest<any[]>(`/findings/job/${jobId}`),

    getResources: () => makeRequest<any[]>('/resources'),
    getResourceById: (resourceId: string) => makeRequest<any>(`/resources/${resourceId}`),
    getResourcesByJobId: (jobId: string) => makeRequest<any[]>(`/resources/job/${jobId}`),

    lockUser: (userId: string) => makeRequest<any>(`/admin/users/${userId}/lock`, 'POST'),
    unlockUser: (userId: string) => makeRequest<any>(`/admin/users/${userId}/unlock`, 'POST'),
    setAdminStatus: (userId: string, isAdmin: boolean) => makeRequest<any>(`/admin/users/${userId}/admin`, 'POST', { is_admin: isAdmin }),
    getUsers: () => makeRequest<any[]>('/users', 'GET'),

    getResumes: () => makeRequest<any[]>('/resumes'),
    getResumeById: (resumeId: string) => makeRequest<any>(`/resumes/${resumeId}`),
    getResumesByJobId: (jobId: string) => makeRequest<any[]>(`/jobs/${jobId}/resumes`),
    createResume: (data: any) => makeRequest<any>('/resumes', 'POST', data),
    updateResume: (resumeId: string, data: any) => makeRequest<any>(`/resumes/${resumeId}`, 'PUT', data),
    deleteResume: (resumeId: string) => makeRequest<void>(`/resumes/${resumeId}`, 'DELETE'),

    getEducations: () => makeRequest<EducationResponse[]>('/education'),
    getEducationById: (educationId: string) => makeRequest<any>(`/education/${educationId}`),
    createEducation: (data: any) => makeRequest<any>('/education', 'POST', data),
    updateEducation: (educationId: string, data: any) => makeRequest<any>(`/education/${educationId}`, 'PUT', data),
    deleteEducation: (educationId: string) => makeRequest<void>(`/education/${educationId}`, 'DELETE'),

    getEmployments: () => makeRequest<EmploymentResponse[]>('/employment'),
    getEmploymentById: (employmentId: string) => makeRequest<any>(`/employment/${employmentId}`),
    createEmployment: (data: any) => makeRequest<any>('/employment', 'POST', data),
    updateEmployment: (employmentId: string, data: any) => makeRequest<any>(`/employment/${employmentId}`, 'PUT', data),
    deleteEmployment: (employmentId: string) => makeRequest<void>(`/employment/${employmentId}`, 'DELETE'),
    createEmploymentSkill: (employmentId: string, skill: SkillCreate) => makeRequest<SkillData>(`/employment/${employmentId}/skill`, 'POST', skill ),
    createEmploymentDetail: (employmentId: string, detail: DetailCreate) => makeRequest<DetailData>(`/employment/${employmentId}/detail`, 'POST', detail ),
    getSkills: () => makeRequest<SkillData[]>('/skill'),
    getSkillById: (skillId: string) => makeRequest<any>(`/skill/${skillId}`),
    getSkillsByEducationId: (educationId: string) => makeRequest<SkillData[]>(`/education/${educationId}/skill`),
    getSkillsByEmploymentId: (employmentId: string) => makeRequest<SkillData[]>(`/employment/${employmentId}/skill`),
    getSkillsByProfileSectionId: (profileSectionId: string) => makeRequest<SkillData[]>(`/profile-section/${profileSectionId}/skill`),
    createSkill: (data: SkillData) => makeRequest<SkillData>('/skill', 'POST', data),
    updateSkill: (skillId: string, data: SkillData) => makeRequest<SkillData>(`/skill/${skillId}`, 'PUT', data),
    deleteSkill: (skillId: string) => makeRequest<void>(`/skill/${skillId}`, 'DELETE'),

    getDetails: (): Promise<DetailData[]> => makeRequest('/detail'),
    getDetailById: (detailId: string): Promise<DetailData> => makeRequest(`/detail/${detailId}`),
    getDetailsByEducationId: (educationId: string): Promise<DetailData[]> => makeRequest(`/education/${educationId}/detail`),
    getDetailsByEmploymentId: (employmentId: string): Promise<DetailData[]> => makeRequest(`/employment/${employmentId}/detail`),
    getDetailsByProfileSectionId: (profileSectionId: string): Promise<DetailData[]> => makeRequest(`/profile-section/${profileSectionId}/detail`),
    createDetail: (data: DetailData): Promise<DetailData> => makeRequest('/detail', 'POST', data),
    updateDetail: (detailId: string, data: DetailData): Promise<DetailData> => makeRequest(`/detail/${detailId}`, 'PUT', data),
    deleteDetail: (id: string): Promise<void> => makeRequest(`/detail/${id}`, 'DELETE'),

    getSearchPreferences: () => makeRequest<any>('/search_preference'),
    createOrUpdateSearchPreferences: (data: any) => makeRequest<any>('/search_preference', 'POST', data),
    getProfileSections: () => makeRequest<ProfileSectionResponse[]>('/profile-section'),
    getProfileSectionById: (profileSectionId: string) => makeRequest<ProfileSectionResponse>(`/profile-section/${profileSectionId}`),
    createProfileSection: (data: {
      title: string;
      section_key?: string | null;
      attributes?: ProfileSectionAttribute[];
    }) =>
      makeRequest<ProfileSectionResponse>('/profile-section', 'POST', data),
    updateProfileSection: (profileSectionId: string, data: {
      title: string;
      section_key?: string | null;
      attributes?: ProfileSectionAttribute[];
    }) =>
      makeRequest<ProfileSectionResponse>(`/profile-section/${profileSectionId}`, 'PUT', data),
    deleteProfileSection: (profileSectionId: string) => makeRequest<void>(`/profile-section/${profileSectionId}`, 'DELETE'),

    // Pitch Analysis API methods
    getPitchAnalyses: () => makeRequest<PitchAnalysisResponse[]>('/pitch-analysis'),
    getPitchAnalysesByJob: (jobId: string) => makeRequest<PitchAnalysisResponse[]>(`/pitch-analysis/job/${jobId}`),
    getPitchAnalysis: (analysisId: string) => makeRequest<PitchAnalysisResponse>(`/pitch-analysis/${analysisId}`),
    createPitchAnalysis: (data: PitchAnalysisCreate) => makeRequest<PitchAnalysisResponse>('/pitch-analysis', 'POST', data),
    transcribePitchAudio: (formData: FormData) => {
      const headers: Record<string, string> = {};
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
      return fetch(`${API_BASE_URL}/pitch-analysis/stt`, {
        method: 'POST',
        headers,
        body: formData,
      }).then(async response => {
        if (!response.ok) {
          throw new Error(getApiStatusMessage(response.status));
        }
        return await response.text(); // STT endpoint returns plain text
      });
    },

    // Subscriptions
    getMySubscription: () => makeRequest<SubscriptionGetResponse>(`/subscriptions/me`, 'GET'),
    createSubscriptionCheckout: (plan: 'monthly' | 'weekly' | 'quarterly' = 'monthly') =>
      makeRequest<SubscriptionCheckoutResponse>(`/subscriptions/checkout`, 'POST', { plan }),
    markOneWeekOfferSeen: () =>
      makeRequest<MarkOneWeekOfferSeenResponse>(`/subscriptions/one-week/mark-seen`, 'POST'),
    cancelSubscription: (subscriptionId: string) =>
      makeRequest<SubscriptionActionResponse>(`/subscriptions/${subscriptionId}/cancel`, 'POST'),
    resumeSubscription: (subscriptionId: string) =>
      makeRequest<SubscriptionActionResponse>(`/subscriptions/${subscriptionId}/resume`, 'POST'),

    // Salary negotiation API
    getScenarios: () =>
      makeRequest<ScenarioInfo[]>('/salary-negotiations/scenarios', 'GET', undefined, false),
    getPersonalities: () =>
      makeRequest<PersonalityInfo[]>('/salary-negotiations/personalities', 'GET', undefined, false),
    createNegotiation: (data: SalaryNegotiationCreate) =>
      makeRequest<SalaryNegotiationResponse>('/salary-negotiations', 'POST', data),
    getNegotiations: (jobId?: string) => {
      const url = jobId ? `/salary-negotiations?job_id=${jobId}` : '/salary-negotiations';
      return makeRequest<SalaryNegotiationResponse[]>(url);
    },
    getNegotiationById: (id: string) =>
      makeRequest<SalaryNegotiationResponse>(`/salary-negotiations/${id}`),
    sendNegotiationMessage: (id: string, message: string) =>
      makeRequest<SalaryNegotiationResponse>(`/salary-negotiations/${id}/messages`, 'POST', { message }),
    finishNegotiation: (id: string) =>
      makeRequest<SalaryNegotiationResponse>(`/salary-negotiations/${id}/finish`, 'POST'),
    transcribeNegotiationAudio: (formData: FormData) => {
      const headers: Record<string, string> = {};
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
      return fetch(`${API_BASE_URL}/salary-negotiations/stt`, {
        method: 'POST',
        headers,
        body: formData,
      }).then(async response => {
        if (!response.ok) {
          throw new Error(getApiStatusMessage(response.status));
        }
        return await response.text(); // STT endpoint returns plain text
      });
    },

    checkReadiness: async () => {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const response = await fetch(`${API_BASE_URL}/readiness/check`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        console.error('Readiness check failed with status:', response.status);
        throw new Error(getApiStatusMessage(response.status));
      }

      return response.json();
    },

    createProfileFromDocument: (formData: FormData) => {
      const headers: Record<string, string> = {};
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
      return fetch(`${API_BASE_URL}/me/from-document`, {
        method: 'POST',
        headers,
        body: formData,
      }).then(async response => {
        if (!response.ok) {
          console.error(`Failed to create profile from document: ${response.status}`);
          throw new Error(getApiStatusMessage(response.status));
        }
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.indexOf("application/json") !== -1) {
          return await response.json();
        }
        return {};
      });
    },

    // Todo API methods
    getTodos: (includeCompleted: boolean = true, jobId?: string, tags?: string[]) => {
      const params = new URLSearchParams();
      params.append('include_completed', includeCompleted.toString());
      if (jobId) params.append('job_id', jobId);
      if (tags && tags.length > 0) params.append('tags', tags.map(t => t.toLowerCase()).join(','));
      return makeRequest<TodoResponse[]>(`/todos?${params.toString()}`);
    },
    getGeneralTodos: (includeCompleted: boolean = true, tags?: string[]) => {
      const params = new URLSearchParams();
      params.append('include_completed', includeCompleted.toString());
      if (tags && tags.length > 0) params.append('tags', tags.map(t => t.toLowerCase()).join(','));
      return makeRequest<TodoResponse[]>(`/todos/general?${params.toString()}`);
    },
    getTodo: (todoId: string) => makeRequest<TodoResponse>(`/todos/${todoId}`),
    createTodo: (data: TodoCreate) => makeRequest<TodoResponse>('/todos', 'POST', data),
    updateTodo: (todoId: string, data: TodoUpdate) => makeRequest<TodoResponse>(`/todos/${todoId}`, 'PUT', data),
    deleteTodo: (todoId: string) => makeRequest<void>(`/todos/${todoId}`, 'DELETE'),
    reorderTodos: (todoIds: string[], context: string = "global") => makeRequest<void>('/todos/reorder', 'POST', { todo_ids: todoIds, context }),
    getDueSoonTodos: (hoursAhead: number = 24) => {
      const params = new URLSearchParams();
      params.append('hours_ahead', hoursAhead.toString());
      return makeRequest<TodoResponse[]>(`/todos/due-soon?${params.toString()}`);
    },
    getOverdueTodos: () => makeRequest<TodoResponse[]>('/todos/overdue'),
    getUserTags: () => makeRequest<string[]>('/todos/tags'),

    // Article API methods
    getArticles: (category?: string) => {
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      const query = params.toString();
      return makeRequest<ArticleListResponse>(`/articles${query ? `?${query}` : ''}`);
    },
    getArticleCategories: () => makeRequest<string[]>('/articles/categories'),
    getArticle: (slug: string) => makeRequest<ArticleResponse>(`/articles/${slug}`),
    getPublicArticles: (category?: string) => {
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      const query = params.toString();
      return makeRequest<ArticleListResponse>(
        `/public/articles${query ? `?${query}` : ''}`,
        'GET',
        undefined,
        false
      );
    },
    getPublicArticleCategories: () =>
      makeRequest<string[]>('/public/articles/categories', 'GET', undefined, false),
    getPublicArticle: (slug: string) =>
      makeRequest<ArticleResponse>(`/public/articles/${slug}`, 'GET', undefined, false),
  }), [makeRequest]);

  return { api };
};
